import React, { useState } from 'react';
import { Box, Grid, Text, DropButton, Button, Anchor, Collapsible, Toolbar, Tip, Heading } from 'grommet';
import { Link } from 'react-router-dom';
import { 
  Close as CloseIcon, 
  RotateLeft as RotateLeftIcon, 
  RotateRight as RotateRightIcon, 
  Expand as ExpandIcon, 
  Contract as ContractIcon, 
  //Filter as FilterIcon 
} from 'grommet-icons';  // Importing icons

import gridData from '../../data/valuestreams/ValueStreamsGridData.json'; 

export const ValueStreamsGrid = ({ isVerticalGrid = true, isGridExpanded = false }) => {
  const [isVerticalGridState, setIsVerticalGrid] = useState(isVerticalGrid);  // Default grid orientation from prop
  const [openDropButton, setOpenDropButton] = useState(null);
  const [showStreamRows, setShowStreamRows] = useState(isGridExpanded); // Set initial state for L3 rows from prop
  const [icon, setIcon] = useState(<RotateLeftIcon />);  // Default icon for RotateLeft
  const [streamToggleIcon, setStreamToggleIcon] = useState(<ExpandIcon />);  // Default icon for Expand
  const horizontalRowHeight = gridData.Settings?.horizontalRowHeight ?? "75px"; 

  // Toggle between grids and icons
  const handleIconClick = () => {
    setIsVerticalGrid(!isVerticalGridState);  // Toggle between grids
    setIcon(isVerticalGridState ? <RotateRightIcon /> : <RotateLeftIcon />);  // Update icon based on grid orientation
  };

  // Toggle between stream grid expand & collapse (for rows 2 onward)
  const handleStreamToggleIconClick = () => {
    setShowStreamRows(!showStreamRows);  // Toggle between grids
    setStreamToggleIcon(showStreamRows ? <ExpandIcon /> : <ContractIcon />);  // Update icon based on collapse/expand state
  };

  const toggleDropButton = (key) => {
    setOpenDropButton((prevKey) => (prevKey === key ? null : key));
  };

  const handleTempAlert = () => {
    alert('This is a temporary alert');
  };

const renderDropButtonContent = (content, title, closeHandler) => (
  <Box pad="small" background="light-2" width="100%" margin="0" style={{ display: 'flex', flexDirection: 'column' }}>
    {/* Title and Close Button */}
    <Box direction="row" justify="between" align="center" pad={{ bottom: 'small' }}>
      <Text weight="bold">{title}</Text>
      <Button
        icon={<CloseIcon />}
        onClick={closeHandler}  
        a11yTitle="Close"
        plain
      />
    </Box>

    {/* Dropdown Content */}
    {content.map((item, index) => (
      item.type === 'link' ? (
        <Box key={index} as="span" align="start" pad="none">
          <Link to={item.href}>
            <Text size="small">{item.text}</Text>
          </Link>
        </Box>
      ) : (
        <Anchor key={index} href={item.href} onClick={handleTempAlert} size="small" label={item.text} />
      )
    ))}
  </Box>
);


  // Helper function to calculate grid area dynamically based on orientation
  const getGridArea = (gridAreaString, isVerticalGrid) => {
    const [rowStart, colStart, rowEnd, colEnd] = gridAreaString.split(' / ').map(Number);

    if (isVerticalGrid) {
      return `${rowStart} / ${colStart} / ${rowEnd} / ${colEnd}`;
    } else {
      return `${colStart} / ${rowStart} / ${colEnd} / ${rowEnd}`;
    }
  };

  return (
    <Box>
      <Box>
        <Heading size="medium" margin={{ top: 'medium' }}>Explore by HPE <b>Value Stream</b> </Heading>
        <Text size="medium" weight="bold">(Note: Route to Market & Lead to Quote have illustrative drill downs.)</Text>
        <Text size="medium" weight="bold">This is not a GW value stream but including it 'just in case' it triggers any ideas.</Text>
        <Toolbar>
          <Tip content={showStreamRows ? "Hide L3 data" : "Show L3 data"}>
            <Button 
              icon={streamToggleIcon} 
              onClick={handleStreamToggleIconClick} 
              style={{ width: 'auto' }}
            />
          </Tip>

          <Tip content={isVerticalGridState ? "Switch to Horizontal Grid" : "Switch to Vertical Grid"}>
            <Button 
              icon={icon} 
              onClick={handleIconClick} 
              style={{ width: 'auto' }} 
            />
          </Tip>
          {/* Placeholder for future filtering... */}
          {/* <Tip content="Filter">
            <Button 
              icon={<FilterIcon />} 
            />
          </Tip> */}
        </Toolbar>
      </Box>

      <Box width="100%" style={{ overflowX: 'auto' }} >
        <Grid

          columns={isVerticalGridState
            ? Array(gridData.L3[0]?.cells?.length || 0).fill('minmax(80px, 1fr)') 
            : Array(gridData.L3[0]?.cells?.length || 0).fill('minmax(110px, 1fr)')} 
          gap="xxsmall"
          flex="grow"
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateRows: 'auto',
          }}
        >
          {/* Render headers (L1)... */}
          {gridData.L1.map((header, index) => (
            header.isDropButton ? (
              <Box key={index} gridArea={getGridArea(header.gridArea, isVerticalGridState)}>
                <DropButton
                  primary
                  pad="xsmall"
                  label={<Box direction="column" align="start" justify="start" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>{header.text}</Box>}
                  align="start"
                  size="small"
                  onClick={() => toggleDropButton(header.text)}
                  open={openDropButton === header.text}
                  fill={true}
                  dropAlign={{ top: 'bottom', left: 'left' }}
                  width="fit-content"
                  dropContent={renderDropButtonContent(header.dropContent, header.text, () => setOpenDropButton(null))}
                  style={{ borderRadius: '0' }}
                />
              </Box>
            ) : (
              <Box key={index} gridArea={getGridArea(header.gridArea, isVerticalGridState)} background="brand" pad="xsmall">
                <Text size="small" weight="bold">{header.text}</Text>
              </Box>
            )
          ))}

          {/* Render subheaders (L2)... */}
          {gridData.L2.map((subheader, index) => {
            const rowBackground = 'brand';
            const textSize = 'small';  // Text size for the first row

            return subheader.isDropButton ? (
              <Box key={`row-0-${index}`} gridArea={getGridArea(subheader.gridArea, isVerticalGridState)} height={isVerticalGridState ? 'auto' : horizontalRowHeight}>
                <DropButton
                  primary
                  pad="xsmall"
                  label={
                    <Box direction="column" align="start" justify="start" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '0px' }}>
                      {subheader.text}
                    </Box>
                  }
                  align="start"
                  size="small"
                  onClick={() => toggleDropButton(subheader.text)}
                  open={openDropButton === subheader.text}
                  fill={true}
                  dropAlign={{ top: 'bottom', left: 'left' }}
                  width="fit-content"
                  dropContent={renderDropButtonContent(subheader.dropContent, subheader.text, () => setOpenDropButton(null))}
                  style={{ borderRadius: '0' }}
                />
              </Box>
            ) : (
              <Box key={`row-0-${index}`} gridArea={getGridArea(subheader.gridArea, isVerticalGridState)} background={rowBackground} pad="xsmall" height={isVerticalGridState ? 'auto' : horizontalRowHeight}>
                <Text size={textSize} weight="bold">{subheader.text}</Text>
              </Box>
            );
          })}

          {/* Render L3 data as an independently collapsible block with a sub-grid... */}
          <Box gridArea={getGridArea("3 / 1 / 4 / 21", isVerticalGridState)} style={{ display: 'block' }}>
            <Collapsible open={showStreamRows}>
              <Grid 
                columns={isVerticalGridState
                  ? Array(gridData.L3[0]?.cells?.length || 0).fill('minmax(80px, 1fr)') 
                  : Array(gridData.L3[0]?.cells?.length || 0).fill('minmax(110px, 1fr)')} 
                gap="xxsmall"
                flex="grow"
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateRows: 'auto',
                }}
              >
                {(gridData.L3 || []).map((row, rowIndex) => (
                  row.cells.map((cell, cellIndex) => {
                    const rowBackground = cell.text ? 'light-4' : undefined; 
                    const textSize = 'xsmall';
                    return cell.isDropButton ? (
                      <Box 
                        key={`row-${rowIndex}-${cellIndex}`} 
                        gridArea={getGridArea(cell.gridArea, isVerticalGridState)}
                        height={isVerticalGridState ? 'auto' : horizontalRowHeight}
                      >
                        <DropButton
                          primary
                          pad="xsmall"
                          label={
                            <Box 
                              direction="column" 
                              align="start" 
                              justify="start" 
                              style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '0px' }}
                            >
                              {cell.text}
                            </Box>
                          }
                          align="start"
                          size="small"
                          onClick={() => toggleDropButton(cell.text)}
                          open={openDropButton === cell.text}
                          fill={true}
                          dropAlign={{ top: 'bottom', left: 'left' }}
                          width="fit-content"
                          dropContent={renderDropButtonContent(cell.dropContent, cell.text, () => setOpenDropButton(null))}
                          style={{ borderRadius: '0' }}
                        />
                      </Box>
                    ) : (
                      <Box
                        key={`row-${rowIndex}-${cellIndex}`}
                        gridArea={getGridArea(cell.gridArea, isVerticalGridState)}
                        background={rowBackground}
                        pad="xsmall"
                        height={isVerticalGridState ? 'auto' : horizontalRowHeight}
                      >
                        <Text size={textSize}>{cell.text}</Text>
                      </Box>
                    );
                  })
                ))}
              </Grid>
            </Collapsible>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};